import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import adminService, { AdminUserDetail, UserEvent } from '../../services/AdminService';

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetForm, setResetForm] = useState({ newPassword: '', confirmPassword: '' });

  const loadDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getUserDetail(id);
      setDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger le detail utilisateur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const user = detail?.user;
  const isActive = user ? ((user as any).isActive ?? (user as any).is_active ?? true) : false;

  const formatDate = (value?: string | null, withTime = false) => {
    if (!value) return '--';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '--';
    return d.toLocaleString('fr-FR', withTime ? {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    } : {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const roleLabel = (role?: string) => {
    switch (role) {
      case 'admin':
        return 'Administrateur plateforme';
      case 'manager':
        return 'Manager';
      case 'user':
        return 'Utilisateur';
      default:
        return role || '--';
    }
  };

  const statusBadge = useMemo(() => {
    const status = (user as any)?.status || (isActive ? 'active' : 'inactive');
    switch (status) {
      case 'trial':
        return { label: 'Trial', classes: 'bg-amber-100 text-amber-800' };
      case 'past_due':
      case 'expired':
        return { label: 'Expiré', classes: 'bg-red-100 text-red-800' };
      case 'inactive':
      case 'blocked':
        return { label: 'Inactif', classes: 'bg-gray-100 text-gray-700' };
      default:
        return { label: 'Actif', classes: 'bg-green-100 text-green-800' };
    }
  }, [isActive, user]);

  const stringifyMetadata = (metadata?: Record<string, any>) => {
    if (!metadata || Object.keys(metadata).length === 0) return '-';
    try {
      return JSON.stringify(metadata);
    } catch {
      return '[metadata]';
    }
  };

  const handleToggleActive = async () => {
    if (!user) return;
    try {
      setActionLoading(true);
      setError(null);
      setMessage(null);
      await adminService.updateUser(user.email, { is_active: !isActive });
      setMessage(!isActive ? `${user.name} réactivé` : `${user.name} désactivé`);
      await loadDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du changement de statut');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartTrial = async () => {
    if (!user) return;
    try {
      setActionLoading(true);
      setError(null);
      setMessage(null);
      const now = new Date();
      const inSevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      await adminService.updateUser(user.email, {
        trial_started_at: now.toISOString(),
        trial_end_at: inSevenDays.toISOString(),
        trial_used: true,
        subscription_status: (user as any).subscription_status || user.subscriptionStatus || null,
      });
      setMessage(`Période d'essai prolongée jusqu'au ${inSevenDays.toLocaleDateString()}`);
      await loadDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du lancement du trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setError(null);
    setMessage(null);
    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setError('Les mots de passe doivent correspondre');
      return;
    }
    if (resetForm.newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    try {
      setActionLoading(true);
      await adminService.updateUser(user.email, { password: resetForm.newPassword });
      setMessage(`Mot de passe réinitialisé pour ${user.name}`);
      setShowResetModal(false);
      setResetForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setActionLoading(false);
    }
  };

  const usageCounts = detail?.usage.counts30d || {};

  if (loading) {
    return (
      <div className="p-6 text-gray-700">
        Chargement de la fiche utilisateur...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
          {error}
        </div>
        <button
          onClick={() => navigate('/admin/users')}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <button
            onClick={() => navigate('/admin/users')}
            className="text-sm text-blue-600 hover:text-blue-800 mb-2"
          >
            ← Retour à la liste
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
            <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${statusBadge.classes}`}>
              {statusBadge.label}
            </span>
            {user.isBeta && (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                Beta
              </span>
            )}
            {user.isConfirmed && (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                Confirmé
              </span>
            )}
          </div>
          <p className="text-gray-600">{roleLabel(user.role)} • {user.companyName || 'Entreprise non renseignée'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/admin/users', { state: { editUserEmail: user.email } })}
            className="px-3 py-2 border border-gray-300 rounded-md text-gray-800 hover:bg-gray-50"
          >
            ✏️ Modifier
          </button>
          <button
            onClick={handleStartTrial}
            disabled={actionLoading}
            className="px-3 py-2 border border-indigo-200 text-indigo-700 rounded-md hover:bg-indigo-50 disabled:opacity-50"
          >
            Trial +7j
          </button>
          <button
            onClick={handleToggleActive}
            disabled={actionLoading}
            className={`px-3 py-2 rounded-md ${
              isActive
                ? 'border border-red-200 text-red-700 hover:bg-red-50'
                : 'border border-green-200 text-green-700 hover:bg-green-50'
            } disabled:opacity-50`}
          >
            {isActive ? 'Désactiver' : 'Réactiver'}
          </button>
          <button
            onClick={() => setShowResetModal(true)}
            className="px-3 py-2 border border-orange-200 text-orange-700 rounded-md hover:bg-orange-50"
          >
            Reset mot de passe
          </button>
        </div>
      </div>

      {message && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md text-green-700">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border rounded-lg shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Compte</h2>
            <span className="text-xs text-gray-500">ID interne: {user.id}</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Email</span>
              <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline">{user.email}</a>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Téléphone</span>
              {user.phone ? (
                <a href={`tel:${user.phone}`} className="text-blue-600 hover:underline">{user.phone}</a>
              ) : (
                <span className="text-gray-700">--</span>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Rôle</span>
              <span className="text-gray-900">{roleLabel(user.role)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Statut</span>
              <span className={`px-2 py-1 text-xs rounded-full ${statusBadge.classes}`}>{statusBadge.label}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Beta</span>
              <span className="text-gray-900">{user.isBeta ? 'Oui' : 'Non'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Confirmé</span>
              <span className="text-gray-900">{user.isConfirmed ? 'Oui' : 'Non'}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-sm p-4 space-y-2">
          <h2 className="text-lg font-semibold text-gray-900">Entreprise / tenant</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Entreprise</span>
              <span className="text-gray-900">{user.companyName || '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Tenant ID</span>
              <span className="text-gray-900">{(user as any).tenantId ?? '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">User ID</span>
              <span className="text-gray-900">{user.userId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">UUID</span>
              <span className="text-gray-900">{(user as any).uuid || '--'}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-sm p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Accès / Abonnement</h2>
            {user.accessOverride && (
              <span className="px-2 py-1 text-xs rounded-full bg-red-50 text-red-700">
                Override: {user.accessOverride}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Type</span>
              <span className="text-gray-900">
                {(user.subscriptionType || 'standard').toUpperCase()}
                {user.subscriptionStatus ? ` • ${user.subscriptionStatus}` : ''}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Fin d'abonnement</span>
              <span className="text-gray-900">{formatDate(user.subscriptionEndDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Trial</span>
              <span className="text-gray-900">
                {formatDate(user.trialStartedAt)} → {formatDate(user.trialEndAt)}
                {user.trialUsed ? ' (utilisé)' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border rounded-lg shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Activité</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Dernière connexion</span>
              <span className="text-gray-900">{formatDate(user.lastLoginAt, true)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Dernière activité</span>
              <span className="text-gray-900">{formatDate(user.lastActivityAt, true)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Logins (30j)</span>
              <span className="text-gray-900">{usageCounts.loginCount30d ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Actions pricing (30j)</span>
              <span className="text-gray-900">{usageCounts.pricingCount30d ?? 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Usage</h2>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-gray-500">Nb devis total</div>
              <div className="text-3xl font-bold text-gray-900">{detail?.usage.devisCountTotal ?? 0}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 bg-gray-50 rounded-md">
                <div className="text-gray-500">Devis créés (30j)</div>
                <div className="font-semibold text-gray-900">{usageCounts.devisCount30d ?? 0}</div>
              </div>
              <div className="p-2 bg-gray-50 rounded-md">
                <div className="text-gray-500">Exports PDF (30j)</div>
                <div className="font-semibold text-gray-900">{usageCounts.exportPdfCount30d ?? 0}</div>
              </div>
              <div className="p-2 bg-gray-50 rounded-md">
                <div className="text-gray-500">Optimisations (30j)</div>
                <div className="font-semibold text-gray-900">{usageCounts.optimizationCount30d ?? 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Actions rapides</h2>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/admin/users')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-800"
            >
              Revenir à la liste
            </button>
            <button
              onClick={() => setShowResetModal(true)}
              className="w-full px-3 py-2 border border-orange-200 rounded-md text-orange-700 hover:bg-orange-50"
            >
              Réinitialiser le mot de passe
            </button>
            <button
              onClick={handleStartTrial}
              disabled={actionLoading}
              className="w-full px-3 py-2 border border-indigo-200 rounded-md text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
            >
              Trial +7j
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Derniers événements</h2>
          <span className="text-sm text-gray-500">{detail?.recentEvents.length || 0} événements</span>
        </div>
        {detail?.recentEvents.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metadata</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm">
                {detail?.recentEvents.map((evt: UserEvent) => (
                  <tr key={evt.id || `${evt.eventType}-${evt.createdAt}`}>
                    <td className="px-4 py-2 text-gray-900">{evt.eventType}</td>
                    <td className="px-4 py-2 text-gray-700">{formatDate(evt.createdAt, true)}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-pre-line break-words">
                      {stringifyMetadata(evt.metadata)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">Aucun événement récent.</p>
        )}
      </div>

      {showResetModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Réinitialiser le mot de passe</h3>
              <form onSubmit={handleResetPassword}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nouveau mot de passe
                  </label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={resetForm.newPassword}
                    onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer
                  </label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={resetForm.confirmPassword}
                    onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetModal(false);
                      setResetForm({ newPassword: '', confirmPassword: '' });
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    Réinitialiser
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
